<template>
  <!--
    The settings body — used by the App Settings modal (⚙ on surfaces without
    the main page chrome) and by the App Settings subpage of the main page.
  -->
  <div class="space-y-4">
    <!-- App Settings -->
    <section v-if="shows('general')" class="space-y-4">
      <h4 class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
        {{ tt('App Settings', 'Nastavitve aplikacije') }}
      </h4>

      <!-- Theme -->
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm font-medium text-gray-700 dark:text-gray-200">{{ tt('Theme', 'Tema') }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">{{ themeLabel }}</p>
        </div>
        <div class="flex overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
          <button
            v-for="opt in themeOptions"
            :key="opt.value"
            type="button"
            class="px-3 py-1.5 text-xs font-medium transition-colors"
            :class="
              themeStore.mode === opt.value
                ? 'bg-primary-400 text-gray-900'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            "
            @click="themeStore.setTheme(opt.value)">
            {{ tt(opt.label, opt.sl) }}
          </button>
        </div>
      </div>

      <!-- Touch Mode on start -->
      <button
        type="button"
        class="flex w-full items-center justify-between gap-3 text-left"
        @click="toggleStartInTouchMode">
        <span>
          <span class="block text-sm font-medium text-gray-700 dark:text-gray-200">{{
            tt('Start in Touch Mode', 'Začni v načinu dotikalne plošče')
          }}</span>
          <span class="block text-xs text-gray-500 dark:text-gray-400">{{
            tt('Use app as a dedicated touch panel', 'Uporabi aplikacijo kot namensko dotikalno ploščo')
          }}</span>
        </span>
        <span
          class="relative h-6 w-10 flex-shrink-0 rounded-full transition-colors duration-200"
          :class="touchModeStart ? 'bg-primary-400' : 'bg-gray-300 dark:bg-gray-600'">
          <span
            class="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200"
            :class="touchModeStart ? 'translate-x-[18px]' : 'translate-x-0.5'" />
        </span>
      </button>
    </section>

    <!-- Desktop Apps (Electron system bar buttons) -->
    <template v-if="isElectronApp && shows('desktop-apps')">
      <div v-if="!section" class="my-5 border-t border-gray-200 dark:border-gray-700" />
      <section class="space-y-4">
        <h4 class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
          {{ tt('Desktop Apps', 'Namizne aplikacije') }}
        </h4>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {{
            tt(
              'Show app buttons in the system bar — each opens in its own tab next to your servers.',
              'Pokaži gumbe aplikacij v sistemski vrstici — vsak se odpre v svojem zavihku poleg vaših strežnikov.',
            )
          }}
        </p>
        <div v-for="app in desktopAppEntries" :key="app.id" class="flex items-center">
          <button
            type="button"
            class="flex w-full items-center justify-between gap-3 text-left disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="app.comingSoon"
            @click="toggleDesktopApp(app.id)">
            <span class="flex min-w-0 items-center gap-2">
              <span class="material-symbols-outlined text-gray-400 dark:text-gray-500" style="font-size: 18px">{{
                app.icon
              }}</span>
              <span class="min-w-0">
                <span class="block text-sm font-medium text-gray-700 dark:text-gray-200">{{
                  tt(app.label.en, app.label.sl)
                }}</span>
                <span class="block truncate text-xs text-gray-500 dark:text-gray-400">{{
                  tt(app.description.en, app.description.sl)
                }}</span>
              </span>
            </span>
            <span
              class="relative h-6 w-10 flex-shrink-0 rounded-full transition-colors duration-200"
              :class="isAppEnabled(app.id) ? 'bg-primary-400' : 'bg-gray-300 dark:bg-gray-600'">
              <span
                class="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200"
                :class="isAppEnabled(app.id) ? 'translate-x-[18px]' : 'translate-x-0.5'" />
            </span>
          </button>
        </div>

        <!-- The system bar (tab strip) is a renderer of its own and can come up
             empty or stale on its own — a failed load, a crashed renderer, a hot
             update in dev. This rebuilds it, no app restart (electron/tabs.ts). -->
        <button
          type="button"
          class="flex w-full items-center justify-center gap-2 rounded-full border border-gray-200 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          :disabled="systemBarBusy"
          @click="reloadSystemBar">
          <span class="material-symbols-outlined text-lg">refresh</span>
          {{ systemBarStatus || tt('Reload the system bar', 'Znova naloži sistemsko vrstico') }}
        </button>
      </section>
    </template>

    <!-- Touch Panel -->
    <div v-if="!section" class="my-5 border-t border-gray-200 dark:border-gray-700" />
    <section v-if="shows('touch-panel')" class="space-y-4">
      <h4 class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
        {{ tt('Touch Panel', 'Dotikalna plošča') }}
      </h4>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        {{
          tt(
            'Use this device as a dedicated wall-mounted touch panel. Exit requires a PIN.',
            'Uporabite to napravo kot namensko stensko dotikalno ploščo. Za izhod je potreben PIN.',
          )
        }}
      </p>
      <button
        type="button"
        class="flex w-full items-center justify-center gap-2 rounded-full bg-primary-400 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-primary-500"
        @click="emit('enter-touch-mode')">
        <span class="material-symbols-outlined text-lg">touch_app</span>
        {{ tt('Enter Touch Mode', 'Vstopi v način dotikalne plošče') }}
      </button>
    </section>

    <!-- About -->
    <div v-if="!section" class="my-5 border-t border-gray-200 dark:border-gray-700" />
    <section v-if="shows('about')" class="space-y-2">
      <h4 class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
        {{ tt('About', 'O programu') }}
      </h4>
      <p class="text-xs text-gray-500 dark:text-gray-400">{{ brand.platformName }} v{{ appVersion }}</p>

      <!-- ── Updates ─────────────────────────────────────────────────────────
           The affordance the old app menu carried, where people look for it.
           Three environments run this panel, and only one of them has an app
           that updates itself:
             · desktop app  → the real electron-updater flow (useElectronUpdates)
             · hub-served web UI → no app to update; the hub carries the releases
             · cloud web    → served from the cloud, always current
           Each branch says which one it is instead of offering a button that
           cannot do anything. -->
      <p v-if="hubUpdateNotice" class="text-xs text-amber-600 dark:text-amber-400">{{ hubUpdateNotice }}</p>

      <div v-if="isElectronApp" class="space-y-2 pt-1">
        <p class="text-sm font-medium text-gray-700 dark:text-gray-200">
          {{ t('settings.settingsUpdates.appUpdate') }}
        </p>

        <p v-if="desktopUpdateStatus === 'downloading'" class="text-xs text-gray-500 dark:text-gray-400">
          {{ t('settings.settingsUpdates.downloadingUpdate') }} {{ desktopUpdateProgress }}%
        </p>
        <p v-else-if="desktopUpdateStatus === 'downloaded'" class="text-xs text-emerald-600 dark:text-emerald-400">
          {{ t('settings.settingsUpdates.updateReady') }}
        </p>
        <p v-else-if="desktopUpdateStatus === 'up-to-date'" class="text-xs text-emerald-600 dark:text-emerald-400">
          {{ t('settings.settingsUpdates.upToDate') }}
        </p>
        <p v-else-if="desktopUpdateStatus === 'error'" class="break-words text-xs text-red-600 dark:text-red-400">
          {{ t('settings.settingsUpdates.checkFailed') }}: {{ desktopUpdateError }}
        </p>

        <div
          v-if="desktopUpdateStatus === 'downloading'"
          class="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div
            class="h-full bg-primary-400 transition-all duration-300"
            :style="{ width: `${desktopUpdateProgress}%` }" />
        </div>

        <button
          v-if="desktopUpdateStatus === 'downloaded'"
          type="button"
          class="flex w-full items-center justify-center gap-2 rounded-full bg-primary-400 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-primary-500"
          @click="quitAndInstallDesktop">
          <span class="material-symbols-outlined text-lg">system_update</span>
          {{ t('settings.settingsUpdates.restartInstall') }}
        </button>
        <button
          v-else
          type="button"
          class="flex w-full items-center justify-center gap-2 rounded-full bg-primary-400 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-primary-500 disabled:opacity-50"
          :disabled="desktopUpdateStatus === 'checking' || desktopUpdateStatus === 'downloading'"
          @click="checkDesktopUpdates">
          <span class="material-symbols-outlined text-lg">system_update</span>
          {{
            desktopUpdateStatus === 'checking'
              ? t('settings.settingsUpdates.checking')
              : t('settings.settingsUpdates.checkUpdates')
          }}
        </button>
      </div>

      <!-- Hub-served web UI (and the native shell): no app updater exists here,
           so it says so instead of offering a button that cannot do anything. -->
      <p v-else-if="!isCloudApp" class="text-xs text-gray-500 dark:text-gray-400">
        {{
          tt(
            'There is no app updater here — hub software updates are installed in Settings → System.',
            'Tu ni posodabljanja aplikacije — posodobitve programske opreme vozlišča namestite v Nastavitve → Sistem.',
          )
        }}
      </p>

      <!-- Cloud web: nothing here to update. -->
      <p v-else class="text-xs text-gray-500 dark:text-gray-400">
        {{
          tt(
            'You are using the cloud version — it is always up to date. There is nothing to update here.',
            'Uporabljate različico v oblaku — ta je vedno najnovejša. Tu ni ničesar za posodabljanje.',
          )
        }}
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { brand } from '../../config/brand';
import { isCloud, isElectron } from '../../config/runtime';
import {
  DESKTOP_APP_ENTRIES,
  readDesktopAppSettings,
  writeDesktopAppSettings,
  type DesktopAppSettings,
} from '../../config/desktopApps';
import { readStartInTouchMode, writeStartInTouchMode } from '../../config/touchModeStart';
import { useElectronUpdates } from '../../composables/useElectronUpdates';
import { useHubUpdateNotifier } from '../../composables/useHubUpdateNotifier';
import { useThemeStore } from '../../stores/theme';
import { tt } from '../../utils/consoleText';

const props = defineProps<{
  /** Re-read persisted settings whenever this flips to true (modal open). */
  active?: boolean;
  /** Render one section only ('general' | 'desktop-apps' | 'touch-panel' | 'about'). */
  section?: string;
}>();

const emit = defineEmits<{ (event: 'enter-touch-mode'): void }>();

/** No section = the full body (modal); a section = that part only (the page). */
function shows(id: string): boolean {
  return !props.section || props.section === id;
}

const themeStore = useThemeStore();
const { t } = useI18n();

const touchModeStart = ref(readStartInTouchMode());
// Same source the sidebar, the menu drawer and the login screen use. `VITE_APP_VERSION`
// was never defined anywhere in this repo, so the About panel silently fell back to a
// hardcoded '1.0.0' and disagreed with the rest of the UI (which reads __APP_VERSION__,
// injected from package.json by vite.config.ts).
const appVersion = ref(typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev');

function toggleStartInTouchMode(): void {
  touchModeStart.value = !touchModeStart.value;
  writeStartInTouchMode(touchModeStart.value);
}

// ── Desktop apps (Electron system bar buttons) ──────────────────────────────
const isElectronApp = isElectron();
// Cloud web (app.torik.ai and the otto-web /app embed) has no app or hub of its
// own to update; everything else is served by a hub. Read once at setup — the
// runtime config is loaded before the app mounts (src/main.ts).
const isCloudApp = isCloud();
const desktopAppEntries = DESKTOP_APP_ENTRIES;
const desktopAppSettings = ref<DesktopAppSettings>(readDesktopAppSettings());

// Another window/tab may have changed the settings since this panel mounted.
watch(
  () => props.active,
  (active) => {
    if (active) {
      desktopAppSettings.value = readDesktopAppSettings();
      touchModeStart.value = readStartInTouchMode();
    }
  },
);

function toggleDesktopApp(id: string): void {
  const key = id as keyof DesktopAppSettings;
  desktopAppSettings.value = { ...desktopAppSettings.value, [key]: !desktopAppSettings.value[key] };
  writeDesktopAppSettings(desktopAppSettings.value);
}

function isAppEnabled(id: string): boolean {
  return desktopAppSettings.value[id as keyof DesktopAppSettings];
}

// ── App updates (the desktop app) ───────────────────────────────────────────
// The same composable the server menu drawer's "App Update" block reads, so the
// two doors show one state (it is a module-level singleton). All of it is inert
// outside Electron: the composable no-ops without the preload bridge.
const {
  status: desktopUpdateStatus,
  progress: desktopUpdateProgress,
  errorMessage: desktopUpdateError,
  checkForUpdates: checkDesktopUpdates,
  quitAndInstall: quitAndInstallDesktop,
} = useElectronUpdates();

// ── Hub updates (the web UI a hub serves) ───────────────────────────────────
// There is no app binary in a browser, but the hub under it does have releases,
// and this is the composable that already knows how to ask (admin-only on the
// hub side, one check at start then hourly). The desktop app starts it at boot
// (src/App.vue); in a browser nothing did, so the About block is what turns the
// hub's own notice on for the two web environments that have a hub behind them.
const {
  updateAvailable: hubUpdateAvailable,
  latestVersion: hubLatestVersion,
  start: startHubUpdateNotifier,
} = useHubUpdateNotifier();

if (!isElectronApp && !isCloudApp) {
  startHubUpdateNotifier();
}

/** The hub's own release notice — only where a hub is actually behind the app. */
const hubUpdateNotice = computed(() => {
  if (isCloudApp || !hubUpdateAvailable.value || !hubLatestVersion.value) return '';
  const version = hubLatestVersion.value;
  return tt(
    `Hub update available: v${version}. Install it in Settings → System.`,
    `Na voljo je posodobitev vozlišča: v${version}. Namestite jo v Nastavitve → Sistem.`,
  );
});

// ── System bar (the Electron window's tab strip) ────────────────────────────
// The strip is its own renderer, so a strip that came up empty or stale cannot
// fix itself from inside the app. This is the user-reachable way back: the main
// process rebuilds the bar view and re-pushes the state (electron/tabs.ts
// `reloadTabBar`, also on Ctrl+Shift+B).
const systemBarBusy = ref(false);
const systemBarStatus = ref('');

async function reloadSystemBar(): Promise<void> {
  const api = window.electron;
  if (systemBarBusy.value || !api?.reloadTabBar) return;
  systemBarBusy.value = true;
  systemBarStatus.value = tt('Reloading…', 'Znova nalagam …');
  try {
    const ok = await api.reloadTabBar();
    // false = no tabbed window here (the legacy single-window mode answers that).
    systemBarStatus.value =
      ok === false
        ? tt('Not available in this window', 'V tem oknu ni na voljo')
        : tt('System bar reloaded', 'Sistemska vrstica znova naložena');
  } catch {
    systemBarStatus.value = tt('Could not reload the system bar', 'Sistemske vrstice ni bilo mogoče znova naložiti');
  } finally {
    systemBarBusy.value = false;
    window.setTimeout(() => {
      systemBarStatus.value = '';
    }, 4000);
  }
}

const themeOptions = [
  { value: 'dark' as const, label: 'Dark', sl: 'Temna' },
  { value: 'light' as const, label: 'Light', sl: 'Svetla' },
];

const themeLabel = computed(() => {
  const opt = themeOptions.find((o) => o.value === themeStore.mode);
  return opt ? tt(opt.label, opt.sl) : tt('Dark', 'Temna');
});
</script>
